import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Save, Sparkles, Mail, Bell, Plus, X, Eye, Send } from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import type { SectorConfig } from '@/core/models';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { WeeklyReportCard } from '@/components/WeeklyReportCard';
import { UserReportForwardCard } from '@/components/UserReportForwardCard';
import { AppFeaturesCard } from '@/components/AppFeaturesCard';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export const Route = createFileRoute('/sector-config')({
  head: () => ({
    meta: [{ title: 'Sector Config · Replya Internal Backoffice' }],
  }),
  component: SectorConfigPage,
});

function SectorConfigPage() {
  const { selectedTenant } = useAppState();
  const query = useQuery({
    queryKey: ['sectorConfig', selectedTenant?.tenantId],
    queryFn: () => apiService.getSectorConfig(selectedTenant!.tenantId),
    enabled: !!selectedTenant,
  });

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Sector Config"
        subtitle={`Configurazione del settore per ${selectedTenant.name}`}
      />

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : query.isError ? (
        <p className="text-sm text-destructive">
          Errore caricamento: {(query.error as Error).message}
        </p>
      ) : (
        <>
          {query.data && (
            <AppFeaturesCard
              key={`features-${query.dataUpdatedAt}`}
              tenantId={selectedTenant.tenantId}
              features={query.data.features}
            />
          )}
          <SectorConfigForm
            key={query.dataUpdatedAt}
            tenantId={selectedTenant.tenantId}
            config={query.data ?? null}
          />
          {query.data?.weeklyReport && (
            <WeeklyReportCard
              key={`weekly-${query.dataUpdatedAt}`}
              tenantId={selectedTenant.tenantId}
              config={query.data.weeklyReport}
            />
          )}
          {query.data && (
            <UserReportForwardCard
              key={`forward-${query.dataUpdatedAt}`}
              tenantId={selectedTenant.tenantId}
              emails={query.data.userReportForwardEmails ?? []}
            />
          )}
        </>
      )}
    </div>
  );
}

// Segnaposto disponibili nei template (fallback se il backend non li restituisce).
const DEFAULT_PLACEHOLDERS = [
  { token: '{{tenantId}}', description: 'Identificativo del tenant' },
  { token: '{{conversationId}}', description: 'ID della conversazione' },
  { token: '{{messageId}}', description: 'ID del messaggio del chatbot' },
  { token: '{{userMessage}}', description: "Ultimo messaggio dell'utente" },
  { token: '{{botMessage}}', description: 'Messaggio del chatbot col feedback negativo' },
  { token: '{{comment}}', description: "Commento lasciato dall'utente" },
  { token: '{{date}}', description: 'Data e ora del feedback' },
];

// Fallback allineati a services/feedbackAlertService.js — usati solo se il
// backend non restituisce i default (es. versione non aggiornata).
const FALLBACK_DEFAULT_SUBJECT =
  '[Feedback Negativo] Tenant: {{tenantId}} | Conversazione: {{conversationId}}';
const FALLBACK_DEFAULT_BODY = [
  'È stato ricevuto un feedback negativo.',
  '',
  'Tenant: {{tenantId}}',
  'Conversazione: {{conversationId}}',
  'ID messaggio: {{messageId}}',
  '',
  "Messaggio dell'utente:",
  '{{userMessage}}',
  '',
  'Messaggio del chatbot:',
  '{{botMessage}}',
  '',
  'Commento utente:',
  '{{comment}}',
].join('\n');

function SectorConfigForm({ tenantId, config }: { tenantId: string; config: SectorConfig | null }) {
  const queryClient = useQueryClient();
  const [sector, setSector] = useState(config?.sector ?? '');
  const [criteria, setCriteria] = useState(config?.evaluationCriteria.join('\n') ?? '');
  const [customPrompt, setCustomPrompt] = useState(config?.customPrompt ?? '');
  const [feedbackAlertEmails, setFeedbackAlertEmails] = useState<string[]>(
    config?.feedbackAlertEmail?.length ? config.feedbackAlertEmail : [''],
  );
  const [feedbackAlertEnabled, setFeedbackAlertEnabled] = useState(config?.feedbackAlertEnabled ?? false);

  const defaultSubject = config?.feedbackAlertDefaultSubjectTemplate ?? FALLBACK_DEFAULT_SUBJECT;
  const defaultBody = config?.feedbackAlertDefaultBodyTemplate ?? FALLBACK_DEFAULT_BODY;
  // Se il tenant non ha un template personalizzato, il campo parte col testo di
  // default (editabile). In salvataggio, se resta identico al default salviamo
  // stringa vuota così il tenant continua a seguire il default di sistema.
  const [subjectTemplate, setSubjectTemplate] = useState(
    config?.feedbackAlertSubjectTemplate || defaultSubject,
  );
  const [bodyTemplate, setBodyTemplate] = useState(
    config?.feedbackAlertBodyTemplate || defaultBody,
  );
  const [testTo, setTestTo] = useState('');

  const resetTemplatesToDefault = () => {
    setSubjectTemplate(defaultSubject);
    setBodyTemplate(defaultBody);
  };

  const updateEmail = (index: number, value: string) => {
    setFeedbackAlertEmails((emails) => emails.map((e, i) => (i === index ? value : e)));
  };

  const addEmail = () => {
    setFeedbackAlertEmails((emails) => [...emails, '']);
  };

  const removeEmail = (index: number) => {
    setFeedbackAlertEmails((emails) => emails.filter((_, i) => i !== index));
  };

  const mutation = useMutation({
    mutationFn: () =>
      apiService.updateSectorConfig(tenantId, {
        sector,
        evaluationCriteria: criteria
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        customPrompt: customPrompt.trim() || undefined,
        feedbackAlertEmail: feedbackAlertEmails.map((e) => e.trim()).filter(Boolean),
        feedbackAlertEnabled,
        feedbackAlertSubjectTemplate:
          subjectTemplate.trim() === defaultSubject.trim() ? '' : subjectTemplate.trim(),
        feedbackAlertBodyTemplate:
          bodyTemplate.trim() === defaultBody.trim() ? '' : bodyTemplate.trim(),
      }),
    onSuccess: () => {
      toast.success('Configurazione salvata');
      queryClient.invalidateQueries({ queryKey: ['sectorConfig', tenantId] });
    },
    onError: () => {
      toast.error('Errore durante il salvataggio');
    },
  });

  const previewMutation = useMutation({
    mutationFn: () =>
      apiService.previewFeedbackAlert(tenantId, {
        subjectTemplate,
        bodyTemplate,
      }),
    onError: (error) => {
      toast.error(`Errore anteprima: ${(error as Error).message}`);
    },
  });

  const testMutation = useMutation({
    mutationFn: () =>
      apiService.testFeedbackAlert(tenantId, {
        to: testTo.trim() || undefined,
        subjectTemplate,
        bodyTemplate,
      }),
    onSuccess: (data) => {
      if (data.sent) {
        const recipients = Array.isArray(data.to) ? data.to.join(', ') : data.to;
        toast.success(`Email di test inviata a ${recipients}`);
      } else if (data.reason === 'alert_not_configured') {
        toast.error('Nessun destinatario: inserisci un indirizzo qui sotto o salva le email di destinazione.');
      } else {
        toast.error('Invio email di test non riuscito');
      }
    },
    onError: (error) => {
      toast.error(`Errore invio test: ${(error as Error).message}`);
    },
  });

  const placeholders = previewMutation.data?.placeholders?.length
    ? previewMutation.data.placeholders
    : DEFAULT_PLACEHOLDERS;

  return (
    <>
      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-brand" />
            Configurazione AI
          </CardTitle>
          <CardDescription>
            Definisci il settore, i criteri di valutazione e il prompt personalizzato usato dal chatbot.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="sector">Settore</Label>
          <Input
            id="sector"
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            placeholder="Es. Edilizia, Energia, Ceramica..."
            className="rounded-xl bg-surface-2 border-border"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="criteria">Criteri di valutazione</Label>
          <Textarea
            id="criteria"
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
            placeholder="Un criterio per riga"
            rows={6}
            className="rounded-xl bg-surface-2 border-border"
          />
          <p className="text-xs text-muted-foreground">Inserisci un criterio per riga.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="prompt">Prompt personalizzato</Label>
          <Textarea
            id="prompt"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Istruzioni aggiuntive per il modello..."
            rows={8}
            className="rounded-xl bg-surface-2 border-border"
          />
        </div>
      </CardContent>
    </Card>

    <Card className="rounded-2xl border bg-surface">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="h-4 w-4 text-brand" />
          Alert feedback negativi
        </CardTitle>
        <CardDescription>
          Invia un'email di notifica quando un utente lascia un feedback negativo al chatbot.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-xl border border-border bg-surface-2 p-4">
          <div className="space-y-0.5">
            <Label htmlFor="feedback-alert-enabled" className="text-sm font-medium">
              Attiva alert email
            </Label>
            <p className="text-xs text-muted-foreground">
              Se disattivato, nessuna email verrà inviata automaticamente.
            </p>
          </div>
          <Switch
            id="feedback-alert-enabled"
            checked={feedbackAlertEnabled}
            onCheckedChange={setFeedbackAlertEnabled}
          />
        </div>

        <div className="space-y-2">
          <Label>Email di destinazione</Label>
          <div className="space-y-2">
            {feedbackAlertEmails.map((email, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    placeholder="es. support@azienda.it"
                    className="rounded-xl bg-surface-2 border-border pl-9"
                  />
                </div>
                {feedbackAlertEmails.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeEmail(index)}
                    className="rounded-xl shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addEmail} className="rounded-xl">
            <Plus className="mr-2 h-4 w-4" />
            Aggiungi email
          </Button>
          <p className="text-xs text-muted-foreground">
            Indirizzi specifici per questo tenant. Lasciare vuoto per non inviare alert; possono essere inseriti anche con l'invio automatico disattivato, per l'invio manuale dalla pagina Feedback.
          </p>
        </div>

        {/* ── Template email ── */}
        <div className="space-y-4 rounded-xl border border-border bg-surface-2 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Contenuto dell'email</Label>
              <p className="text-xs text-muted-foreground">
                Testo semplice, precompilato col modello di default. Usa i segnaposto qui sotto:
                verranno sostituiti coi dati reali del feedback. Se lasci il testo identico al
                default, il tenant continua a seguire il modello di sistema.
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetTemplatesToDefault}
              className="rounded-xl shrink-0 text-xs"
            >
              Ripristina default
            </Button>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((p) => (
              <span
                key={p.token}
                title={p.description}
                className="rounded-md border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
              >
                {p.token}
              </span>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="alert-subject">Oggetto</Label>
            <Input
              id="alert-subject"
              value={subjectTemplate}
              onChange={(e) => setSubjectTemplate(e.target.value)}
              placeholder="[Feedback Negativo] Tenant: {{tenantId}} | Conversazione: {{conversationId}}"
              className="rounded-xl bg-surface border-border font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="alert-body">Corpo</Label>
            <Textarea
              id="alert-body"
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
              placeholder={
                'È stato ricevuto un feedback negativo.\n\n' +
                "Messaggio dell'utente:\n{{userMessage}}\n\n" +
                'Messaggio del chatbot:\n{{botMessage}}\n\n' +
                'Commento utente:\n{{comment}}'
              }
              rows={12}
              className="rounded-xl bg-surface border-border font-mono text-sm"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => previewMutation.mutate()}
              disabled={previewMutation.isPending}
              className="rounded-xl"
            >
              <Eye className="mr-2 h-4 w-4" />
              {previewMutation.isPending ? 'Generazione...' : 'Anteprima'}
            </Button>
          </div>

          {previewMutation.data && (
            <div className="space-y-2 rounded-xl border border-border bg-surface p-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Oggetto:</span>{' '}
                {previewMutation.data.subject}
              </p>
              <iframe
                title="Anteprima email"
                sandbox=""
                srcDoc={previewMutation.data.html}
                className="h-64 w-full rounded-lg border border-border bg-white"
              />
              <p className="text-[11px] text-muted-foreground">
                Anteprima generata con dati di esempio.
              </p>
            </div>
          )}
        </div>

        {/* ── Invio di test ── */}
        <div className="space-y-3 rounded-xl border border-border bg-surface-2 p-4">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">Invio di test</Label>
            <p className="text-xs text-muted-foreground">
              Invia subito un'email di esempio (oggetto con prefisso <code>[TEST]</code>) usando i
              template qui sopra, anche non salvati. Non richiede l'alert automatico attivo.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="Invia a (opzionale, virgola per più indirizzi)"
                className="rounded-xl bg-surface border-border pl-9"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              className="rounded-xl shrink-0"
            >
              <Send className="mr-2 h-4 w-4" />
              {testMutation.isPending ? 'Invio...' : 'Invia email di test'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Se lasci vuoto il campo, l'email di test va agli indirizzi di destinazione salvati.
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
          >
            <Save className="mr-2 h-4 w-4" />
            {mutation.isPending ? 'Salvataggio...' : 'Salva configurazione'}
          </Button>
        </div>
      </CardContent>
    </Card>
    </>
  );
}
